import React, { useState } from 'react';
import { XIcon, CheckCircle2Icon, InfoIcon, PlusCircleIcon } from 'lucide-react';
interface ChartOption {
  id: string;
  title: string;
  description: string;
  category: string;
  hasData: boolean;
  value?: string;
  subtext?: string;
}
interface AddChartModalProps {
  onClose: () => void;
  onAdd: (chartIds: string[]) => void;
  initialSelectedCharts?: Set<string>;
  renderChart: (chartId: string) => React.ReactNode;
}
export function AddChartModal({
  onClose,
  onAdd,
  initialSelectedCharts = new Set(),
  renderChart
}: AddChartModalProps) {
  const [selectedCharts, setSelectedCharts] = useState<Set<string>>(new Set(initialSelectedCharts));
  const chartOptions: ChartOption[] = [
  // Payments
  {
    id: 'gross-volume',
    title: 'Gross volume',
    description: 'Total payment volume including refunds and disputes',
    category: 'Payments',
    hasData: true,
    value: 'THB 240.90K'
  }, {
    id: 'net-volume',
    title: 'Net volume',
    description: 'Volume after fees, refunds, and disputes',
    category: 'Payments',
    hasData: true,
    value: 'THB 235.20K'
  }, {
    id: 'successful-payments',
    title: 'Successful payments',
    description: 'Number of successful payment transactions',
    category: 'Payments',
    hasData: true,
    value: '48,490'
  }, {
    id: 'failed-payments',
    title: 'Failed payments',
    description: 'Recent payment failures excluding blocked payments',
    category: 'Payments',
    hasData: true
  }, {
    id: 'total-payments',
    title: 'Total payments',
    description: 'Snapshot of all payment intent statuses',
    category: 'Payments',
    hasData: true
  }, {
    id: 'payment-methods',
    title: 'Top payment method categories',
    description: 'Breakdown of payment methods used',
    category: 'Payments',
    hasData: true
  }, {
    id: 'average-acceptance-rate',
    title: 'Payment success rate',
    description: 'Payment acceptance rate by providers',
    category: 'Payments',
    hasData: true,
    value: '72.0%'
  },
  // Refunds
  {
    id: 'refunds-volume',
    title: 'Refunds volume',
    description: 'Total amount of refunds conducted',
    category: 'Refunds',
    hasData: true,
    value: 'THB 12.30K'
  }, {
    id: 'refund-rate',
    title: 'Refund rate',
    description: 'Percentage of payments that were refunded',
    category: 'Refunds',
    hasData: true,
    value: '5.1%'
  },
  // Disputes
  {
    id: 'dispute-count',
    title: 'Dispute count',
    description: 'Number of disputes (excluding RFIs)',
    category: 'Disputes',
    hasData: true,
    value: '42'
  }, {
    id: 'dispute-activity',
    title: 'Dispute activity',
    description: 'Dispute rate relative to successful payments',
    category: 'Disputes',
    hasData: true,
    value: '1.2%'
  }, {
    id: 'disputed-payments',
    title: 'Disputed payments',
    description: 'List of recent disputed transactions',
    category: 'Disputes',
    hasData: true
  },
  // Customers
  {
    id: 'new-customers',
    title: 'New customers',
    description: 'Number of new customers created',
    category: 'Customers',
    hasData: true,
    value: '287'
  }, {
    id: 'spend-per-customer',
    title: 'Spend per customer',
    description: 'Average spend per customer',
    category: 'Customers',
    hasData: true,
    value: 'THB 839.50'
  }, {
    id: 'top-customers',
    title: 'Top customers by spend',
    description: 'Highest spending customers',
    category: 'Customers',
    hasData: true
  },
  // Risk
  {
    id: 'blocked-payments',
    title: 'Blocked payments',
    description: 'Payments blocked due to risk settings',
    category: 'Risk',
    hasData: true,
    value: '68'
  }, {
    id: 'blocked-payments-volume',
    title: 'Blocked payments volume',
    description: 'Volume of blocked payments',
    category: 'Risk',
    hasData: true,
    value: 'THB 18.5K'
  }];
  const categories = ['Payments', 'Refunds', 'Disputes', 'Customers', 'Risk'];
  const toggleChart = (chartId: string) => {
    const newSelected = new Set(selectedCharts);
    if (newSelected.has(chartId)) {
      newSelected.delete(chartId);
    } else {
      newSelected.add(chartId);
    }
    setSelectedCharts(newSelected);
  };
  const selectAll = () => {
    const allChartIds = new Set(chartOptions.map(chart => chart.id));
    setSelectedCharts(allChartIds);
  };
  const unselectAll = () => {
    setSelectedCharts(new Set());
  };
  const handleApply = () => {
    onAdd(Array.from(selectedCharts));
    onClose();
  };
  const allSelected = selectedCharts.size === chartOptions.length;
  return <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-2051">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col" data-id="element-2052">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200" data-id="element-2053">
          <div className="flex items-center gap-4" data-id="element-2054">
            <h2 className="text-xl font-semibold text-gray-900" data-id="element-2055">Add charts</h2>
            {/* Select All / Unselect All */}
            <button onClick={allSelected ? unselectAll : selectAll} className="text-sm text-blue-600 hover:text-blue-700 font-medium" data-id="element-2056">
              {allSelected ? 'Unselect all' : 'Select all'}
            </button>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded transition-colors" data-id="element-2057">
            <XIcon size={20} className="text-gray-500" data-id="element-2058" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6" data-id="element-2059">
          <div className="space-y-8" data-id="element-2060">
            {categories.map(category => {
            const categoryCharts = chartOptions.filter(chart => chart.category === category);
            return <div key={category} data-id="element-2061">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4" data-id="element-2062">
                    {category}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2063">
                    {categoryCharts.map(chart => <div key={chart.id} className={`relative transition-all ${selectedCharts.has(chart.id) ? 'ring-2 ring-blue-500 rounded-lg' : ''}`} data-id="element-2064">
                        {/* Selection button - top right, positioned absolutely over the chart */}
                        <button onClick={() => toggleChart(chart.id)} className="absolute top-3 right-3 p-1 bg-white hover:bg-gray-100 rounded-full transition-colors z-10 shadow-sm border border-gray-200" data-id="element-2065">
                          {selectedCharts.has(chart.id) ? <CheckCircle2Icon size={20} className="text-blue-600" data-id="element-2066" /> : <PlusCircleIcon size={20} className="text-gray-400" data-id="element-2067" />}
                        </button>

                        {/* Actual chart component - non-interactive */}
                        <div className="pointer-events-none" data-id="element-2068">
                          {renderChart(chart.id)}
                        </div>
                      </div>)}
                  </div>
                </div>;
          })}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200" data-id="element-2069">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-2070">
            Cancel
          </button>
          <button onClick={handleApply} disabled={selectedCharts.size === 0} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2071">
            Apply
          </button>
        </div>
      </div>
    </div>;
}